/**
 * tm/pages/MobileTodo.tsx — 휴대폰 「오늘 할 일」 (/m · 2026-10-12)
 *  지도사 「이동 중에 막막 등록하고 처리했는지 체크해서 지우고 · 운전하면서 등록 · 그날그날 · 캡처 이미지 첨부」
 *  인지공학 교수 3인 검증(운전 주의분산 · 작업기억 · 모바일 상호작용) 반영판:
 *   - 「말로 등록」(음성)은 인식이 잘 안 돼 없앰(2026-10-12 지도사) — 글 · 캡처로만 적음
 *   - 소리 · 진동으로 저장 · 실패 · 끝냄을 알림 — 「소리로 알려 주기」 끄기 가능
 *   - 등록 뒤 10초 「취소」 · 연결이 끊기면 이 휴대폰에 보관했다가 연결되면 올림(적은 것이 사라지지 않게)
 *   - 마감 = 비움(적은 날만 회색 「10/10 적음」 — 빨강은 남이 정한 마감이 지났을 때만) · 「내일 이후」는 접힌 칸 · 저녁엔 「남은 n개 내일로」
 *   - 끝: 빈 동그라미 → 누르면 그 자리에서 초록 체크 · 줄긋기 1.2초(다시 누르면 취소) → 빠짐 · 10초 되돌리기
 *   - 줄을 누르면 펼침(›): 고치기 · 사진 더 · 내일로 · 지우기(화면 안에서 한 번 더) · 사진은 작은 그림으로
 *   - 알림은 아래 띠 한 곳에 하나만
 */
import { useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, CSSProperties, ReactNode } from 'react';
import * as todosApi from '../api-todos';
import type { Staff } from '../components/AssigneeChipPicker';
import type { Todo } from '../types';
import { G, Icon, clamp } from '../portal-ui/tds';
import { NAVY } from './mobileKit';
import { addDays, diffDays, kstToday, md } from '../flow/flowLogic';
import MobileCheck from './MobileCheck';
import { stageOf, waitingNote } from '../todoCheckView';
import PhoneRequestPanel from '../components/PhoneRequestPanel';
import MobileRemind from './MobileRemind';

interface Me { id: string; name: string }

/* ── 소리 · 진동 ── */
let actx: AudioContext | null = null;
function audio(): AudioContext | null {
  try {
    if (!actx) {
      const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
      const C = w.AudioContext || w.webkitAudioContext;
      if (!C) return null;
      actx = new C();
    }
    if (actx.state === 'suspended') void actx.resume();
    return actx;
  } catch { return null; }
}
type Cue = 'start' | 'end' | 'saved' | 'fail' | 'done';
const CUES: Record<Cue, [number, number][]> = {
  start: [[660, 60], [990, 70]], end: [[990, 50], [660, 60]], saved: [[880, 80], [1320, 90]],
  fail: [[220, 250], [0, 90], [220, 250]], done: [[1200, 45]],
};
const VIB: Record<Cue, number[]> = { start: [40], end: [30], saved: [40, 80, 40], fail: [200, 100, 200], done: [30] };
function cue(c: Cue, on: boolean) {
  try { if (navigator.vibrate) navigator.vibrate(VIB[c]); } catch { /* 진동 없는 기기 */ }
  if (!on) return;
  const a = audio();
  if (!a) return;
  let t = a.currentTime + 0.01;
  for (const [f, ms] of CUES[c]) {
    if (f > 0) {
      const o = a.createOscillator(); const g = a.createGain();
      o.type = 'sine'; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.25, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + ms / 1000);
      o.connect(g); g.connect(a.destination); o.start(t); o.stop(t + ms / 1000 + 0.02);
    }
    t += ms / 1000;
  }
}
/** 사진 줄이기 — 휴대폰 캡처(수 MB)를 긴 변 1600px JPEG 로 */
export async function shrink(f: File): Promise<File> {
  if (!/^image\/(jpeg|png|webp)$/.test(f.type) || f.size < 600 * 1024 || typeof createImageBitmap !== 'function') return f;
  try {
    const img = await createImageBitmap(f);
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const cv = document.createElement('canvas');
    cv.width = Math.round(img.width * k); cv.height = Math.round(img.height * k);
    const c2 = cv.getContext('2d');
    if (!c2) return f;
    c2.fillStyle = '#fff'; c2.fillRect(0, 0, cv.width, cv.height);
    c2.drawImage(img, 0, 0, cv.width, cv.height);
    const blob: Blob | null = await new Promise(res => cv.toBlob(b => res(b), 'image/jpeg', 0.85));
    if (!blob || blob.size >= f.size) return f;
    return new File([blob], f.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch { return f; }
}

/* ── 이 휴대폰에 잠깐 보관(연결이 끊겼을 때) ── */
const OUTBOX = 'roum-todo-outbox';
interface Outbox { title: string; at: string; to?: string; check?: boolean }   // to = 맡긴 직원 id · check = 답 받고 확인(없으면 예전 보관분 = 답 받기)
function readOutbox(): Outbox[] { try { const v = JSON.parse(localStorage.getItem(OUTBOX) || '[]') as Outbox[]; return Array.isArray(v) ? v : []; } catch { return []; } }
function writeOutbox(v: Outbox[]) { try { if (v.length) localStorage.setItem(OUTBOX, JSON.stringify(v)); else localStorage.removeItem(OUTBOX); } catch { /* 보관 못 함 */ } }

const isMine = (t: Todo, me: Me | null) => !me || (Array.isArray(t.assignees) && t.assignees.includes(me.id));
/** 내가 맡긴 일 중 내 차례(답 확인 · 내가 안내) — 맨 위 「확인할 일」 (2026-10-22) */
const isCheckTurn = (t: Todo, me: Me | null) => !!me && !!t.needs_check && t.created_by === me.id
  && (t.status === 'review' || (t.status === 'notify' && t.notify_by === 'me'));
const kst = (iso: string | null | undefined) => { const ms = iso ? new Date(iso).getTime() : NaN; return isNaN(ms) ? '' : new Date(ms + 9 * 3600 * 1000).toISOString(); };

interface Pick { file: File; url: string }
interface Bar { key: number; text: string; actions: { label: string; on: () => void }[] }

export default function MobileTodo({ me, staff }: { me: Me | null; staff: Staff[] | null }) {
  const today = kstToday();
  const [items, setItems] = useState<Todo[] | null>(null);
  const [doneToday, setDoneToday] = useState<Todo[]>([]);
  const [checks, setChecks] = useState<Todo[]>([]);
  const [outbox, setOutbox] = useState<Outbox[]>(() => readOutbox());
  const [showLater, setShowLater] = useState(false);
  const [showDone, setShowDone] = useState(false);
  /** 내가 맡긴 일(진행 중) — 뭘 시켰는지 잊지 않게 (2026-10-23 지도사) · 내 차례인 것은 맨 위 「확인할 일」에 있어 뺀다 */
  const [given, setGiven] = useState<Todo[]>([]);
  const [showGiven, setShowGiven] = useState(true);
  const [text, setText] = useState('');
  const [picks, setPicks] = useState<Pick[]>([]);
  const [busy, setBusy] = useState(false);
  const [upProg, setUpProg] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [askDel, setAskDel] = useState<string | null>(null);
  const [edit, setEdit] = useState<{ id: string; text: string } | null>(null);
  /** 전화로 받은 팀장님 요청 — 펼친 줄에서 「팀장님 요청으로 등록」(2026-10-25 지도사) */
  const [reqFor, setReqFor] = useState<string | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [finishing, setFinishing] = useState<Record<string, boolean>>({});
  const [locked, setLocked] = useState(false);
  const [bar, setBar] = useState<Bar | null>(null);
  const [sound, setSound] = useState<boolean>(() => { try { return localStorage.getItem('roum-todo-sound') !== '0'; } catch { return true; } });
  const finishT = useRef<Record<string, number>>({});
  const doneReq = useRef<Record<string, Promise<unknown>>>({});
  const undone = useRef<Set<string>>(new Set());
  const busyRef = useRef(false);
  const picksRef = useRef<Pick[]>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const moreRef = useRef<HTMLInputElement | null>(null);
  const moreFor = useRef<string | null>(null);
  useEffect(() => { picksRef.current = picks; }, [picks]);
  useEffect(() => { busyRef.current = busy; }, [busy]);
  useEffect(() => { try { localStorage.setItem('roum-todo-sound', sound ? '1' : '0'); } catch { /* 보관 못 함 */ } }, [sound]);

  const nameOf = (id: string) => ((staff || []).find(s => s.id === id) || { name: '' }).name;
  /** 아래 띠 — 한 번에 하나(새 알림이 앞 것을 바꿈) */
  const show = (msg: string, actions: Bar['actions'] = [], ms = actions.length ? 10000 : 3500) => {
    const key = Date.now() + Math.random();
    setBar({ key, text: msg, actions });
    window.setTimeout(() => setBar(b => (b && b.key === key ? null : b)), ms);
  };

  /* ── 불러오기 · 보관함 보내기 ── */
  const load = async () => {
    try {
      const [p, d, gv] = await Promise.all([todosApi.getPendingForMe(), todosApi.getDoneToday(), todosApi.getTodos({ given: true, status: 'open', limit: 100 }).catch(() => null)]);
      // 기한 지난 것 → 기한 가까운 것 → 오래 맡긴 것
      const dueKey = (t: Todo) => (t.due_date ? String(t.due_date).slice(0, 10) : '9999');
      if (gv && gv.success && gv.data) setGiven(gv.data.items.filter(t => !isCheckTurn(t, me)).sort((a, b) => dueKey(a).localeCompare(dueKey(b)) || String(a.created_at || '').localeCompare(String(b.created_at || ''))));
      setItems((p.data || []).filter(t => isMine(t, me) && !isCheckTurn(t, me)));
      setChecks((p.data || []).filter(t => isCheckTurn(t, me)).sort((a, b) => (a.status === 'review' ? 0 : 1) - (b.status === 'review' ? 0 : 1)));
      setDoneToday((d.data || []).filter(t => isMine(t, me)));
    } catch { setItems(x => x || []); show('목록을 못 불러왔어요 · 연결되면 다시 불러올게요'); }
  };
  const flush = async () => {
    const box = readOutbox();
    if (!box.length || !navigator.onLine) return;
    const left: Outbox[] = [];
    for (const o of box) {
      try { const r = await todosApi.createTodo({ title: o.title, assignees: o.to ? [o.to] : me ? [me.id] : undefined, due_date: null, needs_check: !!o.to && (o.check ?? true) }); if (!r.success) left.push(o); }
      catch { left.push(o); }
    }
    writeOutbox(left); setOutbox(left);
    if (left.length < box.length) { show(`보관해 둔 ${box.length - left.length}개를 올렸어요`); void load(); }
  };
  useEffect(() => {
    void load(); void flush();
    const on = () => { void flush(); void load(); };
    const vis = () => { if (document.visibilityState === 'visible') on(); };
    window.addEventListener('online', on);
    document.addEventListener('visibilitychange', vis);
    return () => { window.removeEventListener('online', on); document.removeEventListener('visibilitychange', vis); };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 화면 켜 두기(이 화면에 있는 동안 · 안 되는 기기는 그대로) ── */
  useEffect(() => {
    type Lock = { release: () => Promise<void> };
    let lock: Lock | null = null;
    const nav = navigator as unknown as { wakeLock?: { request: (t: 'screen') => Promise<Lock> } };
    const get = () => { if (nav.wakeLock && document.visibilityState === 'visible') nav.wakeLock.request('screen').then(l => { lock = l; }).catch(() => undefined); };
    get();
    document.addEventListener('visibilitychange', get);
    return () => { document.removeEventListener('visibilitychange', get); if (lock) void lock.release().catch(() => undefined); };
  }, []);

  /* ── 등록 ── */
  /** 적은 글이 묻거나 알아보라는 일이면 「답 받기」를 채워 눈에 띄게(누르는 건 지도사 · 잘못 「시키기」를 눌러 안내가 빠지는 것을 줄임) */
  const askLike = /(팀장|지점장|본부장|사업단장|센터장|대표|설계사)님|문의|물어|알아보|알려|확인해/.test(text);
  /** 맡길 직원 — 일일 업무 담당자 중 나 아닌 사람(지금은 오재인 님 한 명) */
  const other = (staff || []).find(s => me && s.id !== me.id) || null;
  /** to 없으면 내 일 · check = 답 받고 확인(묻는 일) / 아니면 그냥 시키는 일(2026-10-22 지도사 「그냥 하라고 시키는 일도 있다」) */
  const add = async (raw: string, to: Staff | null = null, check = false) => {
    const title = raw.trim();
    const files = picksRef.current;
    if (!title && !files.length) { show('할 일을 적거나 사진을 고르세요'); return; }
    if (busyRef.current) {   // 앞 등록이 올라가는 중 — 적은 글은 칸에 그대로 둔다
      cue('fail', sound);
      show('앞의 등록이 올라가는 중이에요 · 끝나면 「등록」을 눌러 주세요');
      return;
    }
    busyRef.current = true; setBusy(true);
    setText('');
    setPicks([]);
    const name = title || `사진 메모 · ${md(today)} ${kst(new Date().toISOString()).slice(11, 16)}`;
    try {
      const r = await todosApi.createTodo({ title: name, assignees: to ? [to.id] : me ? [me.id] : undefined, due_date: null, needs_check: !!to && check });
      if (!r.success || !r.data) throw new Error(r.error || 'fail');
      let created = r.data;
      let fail = 0;
      for (let i = 0; i < files.length; i += 1) {
        setUpProg(`${i + 1}/${files.length}`);
        try {
          const u = await todosApi.uploadAttachment(created.id, await shrink(files[i].file));
          if (u.success && u.data) created = { ...created, attachments: [...(created.attachments || []), u.data] }; else fail += 1;
        } catch { fail += 1; }
      }
      files.forEach(p => URL.revokeObjectURL(p.url));
      const done = created;
      if (!to) setItems(list => [done, ...(list || [])]);
      cue('saved', sound);
      show(`${to ? (check ? `${to.name} 님께 맡겼어요 · 답이 오면 문자로 알려요` : `${to.name} 님께 시켰어요`) : `등록 · ${name}`}${fail ? ` · 사진 ${fail}장 못 올림` : files.length ? ` · 사진 ${files.length}장` : ''}`, [
        { label: '취소', on: () => { void undoAdd(done); } },
      ]);
    } catch {
      // 연결이 끊겼으면 이 휴대폰에 보관 → 연결되면 올림(사진은 보관 못 해 칸으로 되돌림)
      if (title) { const box = [...readOutbox(), { title, at: new Date().toISOString(), ...(to ? { to: to.id, check } : {}) }]; writeOutbox(box); setOutbox(box); }
      setPicks(files);
      cue('fail', sound);
      show(title ? '아직 못 올렸어요 · 이 휴대폰에 보관했다가 연결되면 올려요' : '사진을 못 올렸어요 · 다시 「등록」을 눌러 주세요');
    } finally { busyRef.current = false; setBusy(false); setUpProg(''); }
  };
  const undoAdd = async (t: Todo) => {
    setBar(null);
    const r = await todosApi.deleteTodo(t.id).catch(() => ({ success: false }));
    if (r.success) setItems(l => (l || []).filter(x => x.id !== t.id));
    else show('취소하지 못했어요 · 줄을 눌러 지워 주세요');
  };

  /* ── 사진 ── */
  const takeFiles = (list: File[]) => {
    const imgs = list.filter(f => /^image\//.test(f.type) || /\.(heic|heif)$/i.test(f.name));
    if (imgs.length) setPicks(ps => [...ps, ...imgs.map(file => ({ file, url: URL.createObjectURL(file) }))].slice(0, 10));
  };
  const onPaste = (e: ClipboardEvent) => {
    const imgs = (Array.from((e.clipboardData && e.clipboardData.files) || []) as File[]).filter(f => /^image\//.test(f.type));
    if (imgs.length) { e.preventDefault(); takeFiles(imgs); }
  };
  const addMore = async (t: Todo, list: File[]) => {
    if (!list.length) return;
    let next = t; let fail = 0;
    for (let i = 0; i < list.length; i += 1) {
      setUpProg(`${i + 1}/${list.length}`);
      try { const u = await todosApi.uploadAttachment(t.id, await shrink(list[i])); if (u.success && u.data) next = { ...next, attachments: [...(next.attachments || []), u.data] }; else fail += 1; } catch { fail += 1; }
    }
    setUpProg('');
    const done = next;
    setItems(l => (l || []).map(x => (x.id === t.id ? done : x)));
    show(fail ? `사진 ${fail}장은 못 올렸어요 · 「사진 더」로 다시` : '사진을 붙였어요');
  };

  /* ── 끝 · 되돌리기 ── */
  const finish = (t: Todo) => {
    if (finishing[t.id]) {   // 1.2초 안에 다시 누르면 취소
      window.clearTimeout(finishT.current[t.id]);
      setFinishing(f => { const n = { ...f }; delete n[t.id]; return n; });
      return;
    }
    cue('done', sound);
    setFinishing(f => ({ ...f, [t.id]: true }));
    finishT.current[t.id] = window.setTimeout(() => {
      setLocked(true); window.setTimeout(() => setLocked(false), 300);   // 다음 줄이 손가락 밑으로 올라와 눌리지 않게
      setFinishing(f => { const n = { ...f }; delete n[t.id]; return n; });
      setItems(l => (l || []).filter(x => x.id !== t.id));
      setOpen(o => (o === t.id ? null : o));
      undone.current.delete(t.id);
      doneReq.current[t.id] = todosApi.updateTodo(t.id, { status: 'done' }).then(r => {
        if (!r.success) throw new Error('fail');
        if (!undone.current.has(t.id)) setDoneToday(d => [{ ...t, status: 'done', completed_at: new Date().toISOString() }, ...d.filter(x => x.id !== t.id)]);
      }).catch(() => { setItems(l => [t, ...(l || []).filter(x => x.id !== t.id)]); show('끝 처리를 못 했어요 · 다시 눌러 주세요'); });
      const by = me && t.created_by !== me.id ? nameOf(t.created_by) : '';
      show(`끝냄 · ${by ? `${by}에게 완료로 보여요` : t.title}`, [{ label: '되돌리기', on: () => { void reopen(t); } }]);
    }, 1200);
  };
  const reopen = async (t: Todo) => {
    setBar(null);
    undone.current.add(t.id);
    const pend = doneReq.current[t.id] as Promise<unknown> | undefined;
    if (pend) await pend.catch(() => undefined);   // 끝 요청이 끝난 뒤에(순서가 뒤바뀌지 않게)
    const r = await todosApi.updateTodo(t.id, { status: 'pending' }).catch(() => ({ success: false }));
    if (!r.success) { show('되돌리지 못했어요'); return; }
    setDoneToday(d => d.filter(x => x.id !== t.id));
    setItems(l => [{ ...t, status: 'pending', completed_at: null }, ...(l || []).filter(x => x.id !== t.id)]);
  };

  /* ── 내일로 · 고치기 · 지우기 ── */
  const moveDue = async (ts: Todo[], due: string | null, label: string) => {
    const before = ts.map(t => ({ id: t.id, due: t.due_date }));
    const oks = await Promise.all(ts.map(t => todosApi.updateTodo(t.id, { due_date: due }).then(r => r.success).catch(() => false)));
    const moved = ts.filter((_, i) => oks[i]).map(t => t.id);
    setItems(l => (l || []).map(x => (moved.includes(x.id) ? { ...x, due_date: due } : x)));
    setOpen(null);
    if (!moved.length) { show('옮기지 못했어요'); return; }
    show(label, [{ label: '되돌리기', on: () => {
      setBar(null);
      void Promise.all(before.filter(b => moved.includes(b.id)).map(b => todosApi.updateTodo(b.id, { due_date: b.due }).catch(() => null)))
        .then(() => setItems(l => (l || []).map(x => { const b = before.find(y => y.id === x.id); return b && moved.includes(x.id) ? { ...x, due_date: b.due } : x; })));
    } }]);
  };
  const saveEdit = async () => {
    if (!edit || !edit.text.trim()) return;
    const cur = edit;
    const r = await todosApi.updateTodo(cur.id, { title: cur.text.trim() }).catch(() => ({ success: false }));
    if (!r.success) { show('고치지 못했어요'); return; }
    const fix = (l: Todo[]) => l.map(x => (x.id === cur.id ? { ...x, title: cur.text.trim() } : x));
    setItems(l => fix(l || [])); setGiven(fix);   // 내 할 일 · 맡긴 일 어디서 고쳐도 같은 한 벌(2026-10-27 지도사 「맡긴 일 제목 수정도」)
    setEdit(null); show('고쳤어요');
  };
  const remove = async (t: Todo) => {
    const r = await todosApi.deleteTodo(t.id).catch(() => ({ success: false, error: '' }));
    if (!r.success) { show(r.error || '지우지 못했어요'); return; }
    setItems(l => (l || []).filter(x => x.id !== t.id));
    setOpen(null); setAskDel(null); show('지웠어요');
  };
  const openAtt = async (t: Todo, attId: string) => {
    const w = window.open('', '_blank');   // 누르는 순간 창부터(뒤늦은 새 창은 막힘 · 이 화면은 그대로)
    const u = await todosApi.getAttachmentUrl(t.id, attId).catch(() => null);
    if (u && u.success && u.data) { if (w) w.location.href = u.data.url; else window.location.href = u.data.url; }
    else { if (w) w.close(); show('파일을 열 수 없어요'); }
  };
  const loadThumbs = (ts: Todo[]) => {
    ts.forEach(t => (t.attachments || []).filter(a => /^image\//.test(a.mimeType) && !urls[a.id]).forEach(a => {
      void todosApi.getAttachmentUrl(t.id, a.id).then(u => { if (u.success && u.data) { const url = u.data.url; setUrls(m => ({ ...m, [a.id]: url })); } }).catch(() => undefined);
    }));
  };

  /* ── 나누기 · 정렬 ── */
  const all = items || [];
  const dueOf = (t: Todo) => (t.due_date ? String(t.due_date).slice(0, 10) : '');
  const nowList = all.filter(t => !dueOf(t) || dueOf(t) <= today).sort((a, b) => {
    const da = dueOf(a); const db = dueOf(b);
    const ra = da && da < today ? 0 : da === today ? 1 : 2; const rb = db && db < today ? 0 : db === today ? 1 : 2;
    return ra - rb || (ra < 2 ? da.localeCompare(db) : String(b.created_at).localeCompare(String(a.created_at)));
  });
  const later = all.filter(t => dueOf(t) > today).sort((a, b) => dueOf(a).localeCompare(dueOf(b)));
  useEffect(() => { loadThumbs(nowList.slice(0, 20)); }, [items]);   // eslint-disable-line react-hooks/exhaustive-deps
  const evening = Number(kst(new Date().toISOString()).slice(11, 13)) >= 18;
  const myLeft = nowList.filter(t => !me || t.created_by === me.id);

  /** 줄 아래 회색 꼬리 — 언제 적었나 · 남이 정한 마감 · 누가 준 요청 */
  const meta = (t: Todo): ReactNode[] => {
    const out: ReactNode[] = [];
    const d = dueOf(t);
    if (d && d < today) out.push(<span key="d" style={{ color: '#B91C1C', fontWeight: 600 }}>마감 {-diffDays(today, d)}일 지남</span>);
    else if (d && d > today) out.push(<span key="d">{diffDays(today, d) === 1 ? '내일' : md(d)}</span>);
    else if (d === today && me && t.created_by !== me.id) out.push(<span key="d" style={{ fontWeight: 600, color: G.g800 }}>오늘까지</span>);
    const c = kst(t.created_at);
    if (c) out.push(<span key="c">{c.slice(0, 10) === today ? `오늘 ${c.slice(11, 16)}` : `${md(c.slice(0, 10))} 적음`}</span>);
    if (me && t.created_by !== me.id) out.push(<span key="r">{nameOf(t.created_by) || '직원'} 요청</span>);
    return out;
  };

  const btn: CSSProperties = { minHeight: 44, padding: '0 12px', borderRadius: 12, border: `1px solid ${G.g400}`, background: '#fff', color: G.g800, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 };
  /** 제목 고치기 칸 — 내 할 일 · 맡긴 일이 같이 쓴다 */
  const editBox = (t: Todo) => (
    <div style={{ display: 'grid', gap: 8 }}>
      <input className="tds-in" value={edit ? edit.text : ''} autoFocus onChange={e => setEdit({ id: t.id, text: e.target.value })} onKeyDown={e => { if (e.key === 'Enter' && !(e.nativeEvent as KeyboardEvent).isComposing) void saveEdit(); }}
        aria-label="제목 고치기" style={{ width: '100%', height: 48, padding: '0 12px', borderRadius: 12, border: `1px solid ${G.g500}`, fontSize: 17, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
        <button type="button" className="tds-btn" onClick={() => setEdit(null)} style={btn}>그만두기</button>
        <button type="button" className="tds-btn" onClick={() => void saveEdit()} style={{ ...btn, border: 'none', background: NAVY, color: '#fff' }}>저장</button>
      </div>
    </div>
  );

  const row = (t: Todo) => {
    const isOpen = open === t.id;
    const fin = !!finishing[t.id];
    const own = !me || t.created_by === me.id;
    const imgs = (t.attachments || []).filter(a => /^image\//.test(a.mimeType));
    const thumb = imgs[0] ? urls[imgs[0].id] : '';
    const m = meta(t);
    return (
      <div key={t.id} style={{ borderBottom: `1px solid ${G.g200}`, background: isOpen ? G.g50 : 'transparent', margin: '0 -8px', padding: '0 8px', borderRadius: isOpen ? 12 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 72 }}>
          <button type="button" className="tds-btn" onClick={() => finish(t)} aria-label={fin ? `끝 취소: ${t.title}` : `끝: ${t.title}`} aria-pressed={fin}
            style={{ width: 56, height: 56, flexShrink: 0, borderRadius: '50%', border: fin ? 'none' : `2px solid ${G.g600}`, background: fin ? '#05864F' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            {fin && <Icon name="check" size={26} color="#fff" sw={2.6} />}
          </button>
          <button type="button" className="tds-row" onClick={() => { setOpen(isOpen ? null : t.id); setAskDel(null); setEdit(null); setReqFor(null); }} aria-expanded={isOpen}
            style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', border: 'none', background: 'none', padding: '10px 0', cursor: 'pointer', fontFamily: 'inherit' }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 600, color: fin ? G.g500 : t.title ? G.g900 : G.g500, textDecoration: fin ? 'line-through' : 'none', lineHeight: 1.4, wordBreak: 'keep-all', ...clamp(isOpen ? 6 : 2) }}>{t.title || '(제목 없음)'}</span>
              {m.length > 0 && <span style={{ display: 'flex', gap: 8, marginTop: 2, fontSize: 14, color: G.g600, ...clamp(1) }}>{m}</span>}
            </span>
            {thumb && <img src={thumb} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0, border: `1px solid ${G.g200}` }} />}
            <span style={{ width: 20, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}><Icon name={isOpen ? 'u' : 'd'} size={18} color={G.g500} sw={2} /></span>
          </button>
        </div>
        {isOpen && (
          <div style={{ padding: '0 0 14px 68px' }}>
            {t.description && <div style={{ fontSize: 15, color: G.g700, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', overflowWrap: 'anywhere', marginBottom: 10, ...clamp(8) }}>{t.description}</div>}
            {(t.attachments || []).length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {(t.attachments || []).map(a => (/^image\//.test(a.mimeType)
                  ? <button key={a.id} type="button" onClick={() => void openAtt(t, a.id)} aria-label={`사진 보기 ${a.fileName}`} style={{ width: 84, height: 84, padding: 0, border: `1px solid ${G.g200}`, borderRadius: 12, overflow: 'hidden', background: G.g100, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {urls[a.id] ? <img src={urls[a.id]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <Icon name="img" size={24} color={G.g500} />}
                  </button>
                  : <button key={a.id} type="button" onClick={() => void openAtt(t, a.id)} style={{ ...btn, border: 'none', background: G.g100, maxWidth: '100%' }}><Icon name="doc" size={16} color={G.g700} /><span style={{ ...clamp(1), maxWidth: 180 }}>{a.fileName}</span></button>))}
              </div>
            )}
            {edit && edit.id === t.id ? editBox(t) : askDel === t.id ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontSize: 15, color: '#B91C1C', fontWeight: 600 }}>정말 지울까요? 끝낸 기록도 안 남아요</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
                  <button type="button" className="tds-btn" onClick={() => setAskDel(null)} style={btn}>아니요</button>
                  <button type="button" className="tds-btn" onClick={() => void remove(t)} style={{ ...btn, borderColor: '#B91C1C', color: '#B91C1C' }}>지우기</button>
                </div>
              </div>
            ) : reqFor === t.id ? (
              <PhoneRequestPanel todoId={t.id} title={t.title} onClose={() => setReqFor(null)} onDone={(msg) => { cue('saved', sound); show(msg); }} />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
                {/* 팀장님이 전화로 맡긴 일 — 업무로 만들어 팀장님 고객관리 「맡긴 일」에 바로 보이게 */}
                <button type="button" className="tds-btn" onClick={() => setReqFor(t.id)} style={{ ...btn, gridColumn: '1 / -1', borderColor: NAVY, color: NAVY, fontWeight: 700 }}>팀장님 요청으로 등록</button>
                {own ? <button type="button" className="tds-btn" onClick={() => setEdit({ id: t.id, text: t.title })} style={btn}>고치기</button> : <span />}
                <button type="button" className="tds-btn" disabled={!!upProg} onClick={() => { moreFor.current = t.id; if (moreRef.current) moreRef.current.click(); }} style={btn}>
                  <Icon name="img" size={16} color={G.g800} />{upProg && moreFor.current === t.id ? `올리는 중 ${upProg}` : '사진 더'}
                </button>
                {own ? <button type="button" className="tds-btn" onClick={() => void moveDue([t], addDays(today, 1), `내일로 · ${t.title}`)} style={btn}>내일로</button> : <span />}
                {own ? <button type="button" className="tds-btn" onClick={() => setAskDel(t.id)} style={{ ...btn, color: '#B91C1C' }}>지우기</button> : <span />}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const fold = (label: string, on: boolean, toggle: () => void) => (
    <button type="button" className="tds-btn" onClick={toggle} aria-expanded={on}
      style={{ width: '100%', minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
      <span style={{ fontSize: 17, fontWeight: 700, color: G.g800 }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 15, color: G.g700 }}>{on ? '접기' : '펼치기'}<Icon name={on ? 'u' : 'd'} size={16} sw={2} /></span>
    </button>
  );

  const ready = !!(text.trim() || picks.length);
  return (
    <div onPaste={onPaste}>
      {/* ── 확인할 일(직원 답 · 내가 안내) — 맨 위 · 문자 링크로 열면 바로 보인다 (2026-10-22) ── */}
      {checks.length > 0 && (
        <div style={{ marginTop: 8, marginBottom: 18 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: G.g900 }}>확인할 일 {checks.length}개</div>
          {checks.map(t => (
            <MobileCheck key={t.id} t={t} myId={me ? me.id : ''} staff={staff || []} show={m => show(m)}
              onChange={n => setChecks(l => (isCheckTurn(n, me) ? l.map(x => (x.id === n.id ? n : x)) : l.filter(x => x.id !== n.id)))} />
          ))}
        </div>
      )}
      <MobileRemind show={m => show(m)} />
      {/* ── 적기(글 · 사진) ── */}
      <textarea className="tds-in" value={text} onChange={e => setText(e.target.value)} rows={2}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as KeyboardEvent).isComposing) { e.preventDefault(); void add(text); } }}
        aria-label="할 일 적기" placeholder="할 일 적기 · 캡처는 붙여 넣어도 돼요"
        style={{ width: '100%', minHeight: 64, marginTop: 8, padding: '12px 14px', borderRadius: 14, border: `1px solid ${G.g500}`, fontSize: 17, lineHeight: 1.5, color: G.g900, boxSizing: 'border-box', resize: 'none', outline: 'none', fontFamily: 'inherit' }} />
      {picks.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
          {picks.map((p, i) => (
            <span key={p.url} style={{ position: 'relative', width: 64, height: 64 }}>
              <img src={p.url} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', display: 'block', border: `1px solid ${G.g200}` }} />
              <button type="button" aria-label="사진 빼기" onClick={() => { URL.revokeObjectURL(p.url); setPicks(ps => ps.filter((_, j) => j !== i)); }}
                style={{ position: 'absolute', top: -12, right: -12, width: 44, height: 44, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                <span style={{ width: 24, height: 24, borderRadius: '50%', background: G.g900, color: '#fff', fontSize: 15, lineHeight: '24px', textAlign: 'center' }}>×</span>
              </button>
            </span>
          ))}
        </div>
      )}
      {/* 사진 · 등록은 예전 자리 그대로(내가 나에게 적는 일은 바뀐 것 없음) · 맡기기는 아래 줄에 따로(2026-10-22 지도사 「동일하면 이상해진다」) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)', gap: 8, marginTop: 10 }}>
        <button type="button" className="tds-btn" onClick={() => fileRef.current && fileRef.current.click()} style={{ ...btn, minHeight: 52 }}>
          <Icon name="img" size={18} color={G.g800} />사진{picks.length ? ` ${picks.length}장` : ''}
        </button>
        <button type="button" className="tds-btn" onClick={() => void add(text)} aria-disabled={!ready}
          style={{ minHeight: 52, borderRadius: 14, border: 'none', background: busy ? G.g400 : ready ? NAVY : G.g100, color: ready || busy ? '#fff' : G.g500, fontSize: 17, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          {busy ? (upProg ? `사진 올리는 중 ${upProg}` : '등록 중') : '등록'}
        </button>
      </div>
      {/* 오재인 님께: 그냥 시키기(직원이 끝냄) · 답 받기(직원 답 → 내가 확인 → 안내) — 2026-10-22 지도사 「그냥 하라고 시키는 일도 있다」 */}
      {other && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8, marginTop: 8 }}>
          <button type="button" className="tds-btn" onClick={() => void add(text, other, false)} aria-disabled={!ready}
            style={{ ...btn, minHeight: 52, borderColor: ready ? NAVY : G.g300, color: ready ? NAVY : G.g500, fontWeight: 700, fontSize: 15, padding: '0 8px' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{other.name} 님께 시키기</span>
          </button>
          <button type="button" className="tds-btn" onClick={() => void add(text, other, true)} aria-disabled={!ready}
            style={{ ...btn, minHeight: 52, borderColor: ready ? NAVY : G.g300, color: ready ? (askLike ? '#fff' : NAVY) : G.g500, background: ready && askLike ? NAVY : '#fff', fontWeight: 700, fontSize: 15, padding: '0 8px' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{other.name} 님께 답 받기</span>
          </button>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={e => { takeFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
      <input ref={moreRef} type="file" accept="image/*" multiple hidden onChange={e => { const t = all.find(x => x.id === moreFor.current); if (t) void addMore(t, Array.from(e.target.files || [])); e.target.value = ''; }} />

      {/* ── 오늘 ── */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '26px 0 2px' }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: G.g900 }}>할 일</span>
        <span style={{ fontSize: 15, color: G.g700 }}>{items === null ? '' : `${nowList.length + outbox.length}개 남음`}</span>
      </div>
      <div style={{ pointerEvents: locked ? 'none' : 'auto' }}>
        {outbox.map((o, i) => (
          <div key={`o${i}`} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 64, borderBottom: `1px solid ${G.g200}` }}>
            <span style={{ width: 56, height: 56, flexShrink: 0, borderRadius: '50%', border: `2px dashed ${G.g400}`, boxSizing: 'border-box' }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 600, color: G.g700, ...clamp(2) }}>{o.title}</span>
              <span style={{ fontSize: 14, color: G.g600 }}>이 휴대폰에 보관 · 연결되면 올려요</span>
            </span>
          </div>
        ))}
        {items === null && <div style={{ padding: '24px 0', fontSize: 16, color: G.g600 }}>불러오는 중이에요</div>}
        {items !== null && nowList.length === 0 && outbox.length === 0 && <div style={{ padding: '24px 0', fontSize: 16, color: G.g700 }}>남은 할 일이 없어요</div>}
        {nowList.map(t => (me && stageOf(t, me.id) !== 'plain'
          ? <MobileCheck key={t.id} t={t} myId={me.id} staff={staff || []} show={m => show(m)}
              onChange={n => setItems(l => (['write', 'staffNotify'].includes(stageOf(n, me.id)) ? (l || []).map(x => (x.id === n.id ? n : x)) : (l || []).filter(x => x.id !== n.id)))} />
          : row(t)))}
      </div>
      {evening && myLeft.length > 0 && (
        <button type="button" className="tds-btn" onClick={() => void moveDue(myLeft, addDays(today, 1), `${myLeft.length}개 내일로 옮겼어요`)} style={{ ...btn, width: '100%', minHeight: 52, marginTop: 12 }}>
          오늘 못 한 {myLeft.length}개 내일로 옮기기
        </button>
      )}

      {/* ── 맡긴 일 — 누구에게 · 어디까지 (2026-10-23 지도사 「내가 시킨 업무 진행 상황도 내 폰에서」) ── */}
      {given.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {fold(`맡긴 일 ${given.length}개`, showGiven, () => setShowGiven(v => !v))}
          {showGiven && given.map(t => {
            const who = (Array.isArray(t.assignees) ? t.assignees : []).filter(id => !me || id !== me.id).map(nameOf).filter(Boolean).join(' · ') || '담당자';
            const wait = waitingNote(t);
            const st = t.needs_check
              ? (t.status === 'notify' ? `${t.notify_to || '안내받을 분'}께 답 전할 차례` : wait || (t.result_at ? '다시 보는 중' : '알아보는 중'))   // wait = 「(어디 ·) 답 기다림 · 10/30 다시 봄」 한 문장(앞에 또 붙이면 「답 기다림 · 답 기다림」 · 2026-10-27)
              : (t.status === 'partial' ? `진행 중${t.progress_note ? ' · ' + t.progress_note : ''}` : '아직 시작 전');
            const late = !!t.due_date && String(t.due_date).slice(0, 10) < today;
            const givenOn = t.created_at ? kst(String(t.created_at)).slice(0, 10) : '';
            return (
              <div key={t.id} style={{ borderBottom: `1px solid ${G.g100}` }}>
                {/* 줄을 누르면 펼쳐 「제목 고치기」(2026-10-27 지도사 「맡긴 일 제목 수정도 가능하게」) — 내 할 일 줄과 같은 펼침 · 같은 고치기 칸 */}
                <button type="button" className="tds-row" aria-expanded={open === t.id} onClick={() => { setOpen(open === t.id ? null : t.id); if (edit && edit.id !== t.id) setEdit(null); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: G.g900, lineHeight: 1.4, wordBreak: 'keep-all', ...clamp(2) }}>{t.title}</div>
                {/* 기한 지난 것을 맨 앞에(빨강) — 줄이 넘치면 뒤(맡긴 날)부터 잘린다 */}
                <div style={{ marginTop: 2, fontSize: 14, color: G.g700, ...clamp(1) }}>
                  {late && <b style={{ fontWeight: 700, color: '#B42318' }}>기한 지남 {md(String(t.due_date).slice(0, 10))} · </b>}
                  {/* 한 줄에 꼭 필요한 것만(2026-10-27 지도사 「복잡」) — 답을 기다리면 「다시 봄」 날이 기한보다 중요 · 오늘 맡긴 일은 맡긴 날을 안 적음 */}
                  <b style={{ fontWeight: 700, color: G.g800 }}>{who}</b> · {st}{t.due_date && !late && !wait ? ` · 기한 ${md(String(t.due_date).slice(0, 10))}` : ''}{givenOn && givenOn !== today ? ` · ${md(givenOn)} 맡김` : ''}
                </div>
                </span>
                <span style={{ width: 20, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}><Icon name={open === t.id ? 'u' : 'd'} size={18} color={G.g500} sw={2} /></span>
                </button>
                {open === t.id && (
                  <div style={{ padding: '0 0 14px' }}>
                    {edit && edit.id === t.id ? editBox(t)
                      : <button type="button" className="tds-btn" onClick={() => setEdit({ id: t.id, text: t.title })} style={{ ...btn, width: '100%' }}>제목 고치기</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── 내일 이후 · 오늘 끝낸 것 ── */}
      {later.length > 0 && <div style={{ marginTop: 16 }}>{fold(`내일 이후 ${later.length}개`, showLater, () => setShowLater(v => !v))}{showLater && later.map(row)}</div>}
      {doneToday.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {fold(`오늘 끝낸 것 ${doneToday.length}개`, showDone, () => setShowDone(v => !v))}
          {showDone && doneToday.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, borderBottom: `1px solid ${G.g100}` }}>
              <span style={{ width: 56, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ width: 32, height: 32, borderRadius: '50%', background: '#05864F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={18} color="#fff" sw={2.6} /></span>
              </span>
              <span style={{ flex: 1, fontSize: 16, color: G.g600, textDecoration: 'line-through', ...clamp(1) }}>{t.title}</span>
              <button type="button" className="tds-btn" onClick={() => void reopen(t)} style={{ ...btn, minHeight: 44 }}>되돌리기</button>
            </div>
          ))}
        </div>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, marginTop: 16, fontSize: 15, color: G.g700, cursor: 'pointer' }}>
        <input type="checkbox" checked={sound} onChange={e => setSound(e.target.checked)} style={{ width: 22, height: 22, margin: 0 }} />
        소리로 알려 주기
      </label>
      <div style={{ height: 24 }} />

      {/* ── 아래에 붙은 띠: 알림 하나 ── */}
      {bar && (
        <div style={{ position: 'sticky', bottom: 0, zIndex: 10, margin: '0 -24px', padding: '10px 16px calc(12px + env(safe-area-inset-bottom, 0px))', background: '#fff', borderTop: `1px solid ${G.g200}` }}>
            <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 56, padding: '6px 6px 6px 14px', borderRadius: 14, background: G.g900, color: '#fff' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 15, ...clamp(2) }}>{bar.text}</span>
              {bar.actions.map(a => (
                <button key={a.label} type="button" onClick={a.on} style={{ minHeight: 44, padding: '0 12px', borderRadius: 10, border: 'none', background: '#fff', color: G.g900, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>{a.label}</button>
              ))}
            </div>
        </div>
      )}
    </div>
  );
}
